
// Intelligent mapping of authorities to their payment/appeal portals.
// This service prioritizes exact matches against a curated list of major UK issuers.

interface CouncilConfig {
  name: string;
  url: string;
  matchKeys: string[]; // Lowercase keywords to identify this council
}

// Major UK Issuers (London Boroughs, Major Cities, Transport Authorities, Private Companies)
const COUNCILS: CouncilConfig[] = [
  // --- Private Parking Operators (New) ---
  {
    name: 'ParkingEye',
    url: 'https://www.parkingeye.co.uk/pay-a-pcn/',
    matchKeys: ['parkingeye', 'parking eye']
  },
  {
    name: 'Euro Car Parks',
    url: 'https://www.eurocarparks.com/pay-a-parking-charge',
    matchKeys: ['euro car parks', 'ecp']
  },
  {
    name: 'APCOA Parking',
    url: 'https://www.apcoa.co.uk/payments/',
    matchKeys: ['apcoa']
  },
  {
    name: 'NCP (National Car Parks)',
    url: 'https://www.ncp.co.uk/help-centre/pcn-payment-and-appeals/?utm_source=letter&utm_campaign=pcn-letter/',
    matchKeys: ['ncp', 'national car parks']
  },
  {
    name: 'Smart Parking',
    url: 'https://www.smartparking.com/motorists/pay-a-pcn',
    matchKeys: ['smart parking']
  },
  {
    name: 'Horizon Parking',
    url: 'https://www.horizonparking.co.uk/pay-a-parking-charge-notice/',
    matchKeys: ['horizon']
  },
  {
    name: 'UK Car Park Management (CPM)',
    url: 'https://www.uk-carparkmanagement.co.uk/pay-charge',
    matchKeys: ['cpm', 'uk car park management']
  },
  {
    name: 'Gemini Parking Solutions',
    url: 'https://pay.geminiparkingsolutions.com/',
    matchKeys: ['gemini parking']
  },
  {
    name: 'Civil Enforcement Ltd',
    url: 'https://www.ce-service.co.uk/',
    matchKeys: ['civil enforcement', 'cel']
  },
  {
    name: 'UK Parking Control',
    url: 'https://paycharge.co.uk/',
    matchKeys: ['ukpc', 'uk parking control']
  },
  {
    name: 'Excel Parking',
    url: 'https://www.excelparking.co.uk/pay-pcn',
    matchKeys: ['excel parking']
  },

  // --- Transport Authorities ---
  {
    name: 'Transport for London',
    url: 'https://tfl.gov.uk/modes/driving/pay-a-pcn',
    matchKeys: ['tfl', 'transport for london', 'congestion', 'ulez', 'lez', 'red route']
  },
  {
    name: 'Dartford Crossing',
    url: 'https://www.gov.uk/pay-dartford-crossing-charge',
    matchKeys: ['dartford', 'dart charge']
  },
  {
    name: 'Merseyflow',
    url: 'https://www.merseyflow.co.uk/pay-pcn',
    matchKeys: ['mersey', 'merseyflow', 'mersey gateway']
  },
  
  // --- London Boroughs (High Volume) ---
  {
    name: 'Westminster City Council',
    url: 'https://www.westminster.gov.uk/parking/pay-penalty-charge-notice-pcn',
    matchKeys: ['westminster']
  },
  {
    name: 'Camden Council',
    url: 'https://www.camden.gov.uk/pay-penalty-charge-notice',
    matchKeys: ['camden']
  },
  {
    name: 'London Borough of Hackney',
    url: 'https://hackney.gov.uk/pay-pcn',
    matchKeys: ['hackney']
  },
  {
    name: 'London Borough of Hammersmith & Fulham',
    url: 'https://www.lbhf.gov.uk/parking/pay-and-challenge-parking-fines',
    matchKeys: ['hammersmith', 'fulham', 'lbhf']
  },
  {
    name: 'London Borough of Islington',
    url: 'https://islington.tarantoportal.com/PCNs',
    matchKeys: ['islington']
  },
  {
    name: 'Royal Borough of Kensington and Chelsea',
    url: 'https://www.rbkc.gov.uk/parking-transport-and-streets/pay-penalty-charge-notice-pcn',
    matchKeys: ['kensington', 'chelsea', 'rbkc']
  },
  {
    name: 'London Borough of Lambeth',
    url: 'https://www.lambeth.gov.uk/parking/pay-penalty-charge-notice',
    matchKeys: ['lambeth']
  },
  {
    name: 'London Borough of Southwark',
    url: 'https://www.southwark.gov.uk/parking/pay-a-parking-fine',
    matchKeys: ['southwark']
  },
  {
    name: 'London Borough of Tower Hamlets',
    url: 'https://www.towerhamlets.gov.uk/lgnl/transport_and_streets/Parking/Penalty_Charge_Notices/Pay_a_PCN.aspx',
    matchKeys: ['tower hamlets']
  },
  {
    name: 'London Borough of Wandsworth',
    url: 'https://www.wandsworth.gov.uk/parking/parking-fines-and-appeals/pay-parking-fine/',
    matchKeys: ['wandsworth']
  },
  {
    name: 'London Borough of Ealing',
    url: 'https://www.ealing.gov.uk/info/201178/parking_fines/1922/pay_a_penalty_charge_notice_pcn',
    matchKeys: ['ealing']
  },
  {
    name: 'London Borough of Barnet',
    url: 'https://www.barnet.gov.uk/parking/parking-and-traffic-enforcement/pay-penalty-charge-notice-pcn',
    matchKeys: ['barnet']
  },
  {
    name: 'London Borough of Brent',
    url: 'https://www.brent.gov.uk/parking-streets-and-transport/parking-tickets-and-permits/pay-a-parking-ticket',
    matchKeys: ['brent']
  },
  {
    name: 'London Borough of Bromley',
    url: 'https://www.bromley.gov.uk/parking/pay-pcn',
    matchKeys: ['bromley']
  },
  {
    name: 'City of London',
    url: 'https://www.cityoflondon.gov.uk/services/parking/pay-a-penalty-charge-notice',
    matchKeys: ['city of london', 'square mile']
  },
  {
    name: 'London Borough of Croydon',
    url: 'https://www.croydon.gov.uk/parking-streets-and-transport/parking/parking-fines-and-appeals/pay-parking-fine',
    matchKeys: ['croydon']
  },
  {
    name: 'London Borough of Haringey',
    url: 'https://www.haringey.gov.uk/parking-roads-and-travel/parking/penalty-charge-notices-pcns/pay-pcn',
    matchKeys: ['haringey']
  },
  {
    name: 'London Borough of Hounslow',
    url: 'https://www.hounslow.gov.uk/info/20108/parking_tickets_and_fines/160/pay_a_parking_fine',
    matchKeys: ['hounslow']
  },
  {
    name: 'London Borough of Newham',
    url: 'https://www.newham.gov.uk/transport-streets/pay-pcn',
    matchKeys: ['newham']
  },
  {
    name: 'London Borough of Waltham Forest',
    url: 'https://www.walthamforest.gov.uk/parking-and-roads/parking-tickets-pcns/pay-parking-ticket-pcn',
    matchKeys: ['waltham forest']
  },

  // --- Major Cities (Outside London) ---
  {
    name: 'Birmingham City Council',
    url: 'https://www.birmingham.gov.uk/paypcn',
    matchKeys: ['birmingham']
  },
  {
    name: 'Manchester City Council',
    url: 'https://www.manchester.gov.uk/payapcn',
    matchKeys: ['manchester']
  },
  {
    name: 'Bristol City Council',
    url: 'https://www.bristol.gov.uk/parking/pay-a-parking-fine',
    matchKeys: ['bristol']
  },
  {
    name: 'Leeds City Council',
    url: 'https://www.leeds.gov.uk/parking-roads-and-travel/parking-tickets',
    matchKeys: ['leeds']
  },
  {
    name: 'Liverpool City Council',
    url: 'https://liverpool.gov.uk/parking-roads-and-travel/parking-tickets/',
    matchKeys: ['liverpool']
  },
  {
    name: 'Nottingham City Council',
    url: 'https://www.nottinghamcity.gov.uk/transport-parking-and-streets/parking-and-permits/pay-or-challenge-a-penalty-charge-notice-pcn/',
    matchKeys: ['nottingham']
  },
  {
    name: 'Sheffield City Council',
    url: 'https://www.sheffield.gov.uk/parking/pay-parking-fine',
    matchKeys: ['sheffield']
  },
  {
    name: 'Glasgow City Council',
    url: 'https://www.glasgow.gov.uk/index.aspx?articleid=18837',
    matchKeys: ['glasgow']
  },
  {
    name: 'Edinburgh Council',
    url: 'https://www.edinburgh.gov.uk/parking-tickets/pay-parking-ticket',
    matchKeys: ['edinburgh']
  },
  {
    name: 'Brighton & Hove City Council',
    url: 'https://www.brighton-hove.gov.uk/parking-and-travel/parking/pay-penalty-charge-notice-pcn',
    matchKeys: ['brighton', 'hove']
  }
];

export const CouncilPaymentService = {
  /**
   * Intelligently resolves the payment/portal URL for a given authority.
   * Matches against a curated database of major issuers using keyword heuristics.
   */
  getPaymentLink: (authorityName: string, _pcnNumber?: string, _vrm?: string): string => {
    if (!authorityName) return 'https://www.google.com/search?q=pay+parking+fine';

    const normalizedAuth = authorityName.toLowerCase();

    // 1. Direct Database Match
    const match = COUNCILS.find(council => 
      council.matchKeys.some(key => normalizedAuth.includes(key))
    );

    if (match) {
      return match.url;
    }

    // 2. Intelligent Fallback (Google Search)
    const cleanName = authorityName
      .replace(/London Borough of/i, '')
      .replace(/City Council/i, '')
      .replace(/Metropolitan Borough/i, '')
      .replace(/Royal Borough of/i, '')
      .replace(/District Council/i, '')
      .replace(/Services Ltd/i, '')
      .replace(/Parking Solutions/i, '')
      .trim();
      
    // Construct a high-intent search query
    return `https://www.google.com/search?q=pay+pcn+${encodeURIComponent(cleanName + ' ' + authorityName)}`;
  },

  /**
   * Alias for getPaymentLink to be used in Liability Transfer contexts.
   * Most councils use a unified portal for Payment and Representations (Appeals/Transfer).
   */
  getPortalLink: (authorityName: string, pcnNumber?: string, vrm?: string): string => {
    return CouncilPaymentService.getPaymentLink(authorityName, pcnNumber, vrm);
  },

  /**
   * Checks if the authority is in our trusted database.
   */
  isKnownAuthority: (authorityName: string): boolean => {
     if (!authorityName) return false;
     const normalizedAuth = authorityName.toLowerCase();
     return COUNCILS.some(council => 
       council.matchKeys.some(key => normalizedAuth.includes(key))
     );
  }
};
